import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import {
  useQuest,
  usePromotion,
  useRequestEstimate,
  useSubmitOrder,
} from "@quest_editor/mocks";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import { getBuildOptionsDisplay } from "@quest_editor/utils/view/buildOptionsDisplay";
import PromotionInfoSection from "./PromotionInfoSection";
import PlaceholderSection from "./PlaceholderSection";
import EstimateSection from "./EstimateSection";

const QuestEstimate = () => {
  const { t } = useTranslation("questEditor");
  const { id: questId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const [isEstimateRequested, setIsEstimateRequested] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (quest) {
      setIsEstimateRequested(quest.estimateRequested);
    }
  }, [quest]);

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const { requestEstimate } = useRequestEstimate();
  const { submitOrder } = useSubmitOrder();
  const { getTypeLabel } = getPromotionLabels();
  const { buildOptionsDisplay } = getBuildOptionsDisplay();

  const handleRequestEstimate = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await requestEstimate({ questId: quest.id });
      setIsEstimateRequested(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitOrder = async () => {
    if (!quest || !promotion || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await submitOrder({ questId: quest.id });
      navigate(`/quest_editor/promotion/${promotion.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="column" spacing={2} alignItems="flex-start">
        <Breadcrumb items={[{ label: promotion.name, to: `/quest_editor/promotion/${promotion.id}` }, { label: quest.name }]} />
        <Stack direction="row" spacing={1} alignItems="center">
          <PromotionTypeChip type={promotion.type} />
          <PromotionStatusChip status={promotion.status} />
        </Stack>
      </Stack>

      <PromotionInfoSection
        quest={quest}
        promotion={promotion}
        getTypeLabel={getTypeLabel}
        buildOptionsDisplay={buildOptionsDisplay}
      />

      <PlaceholderSection />

      <EstimateSection />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/promotion/${promotion.id}`}
        >
          {t("quest_estimate.button.back")}
        </BackButton>
        <Stack direction="row" spacing={2}>
          <ActionButton
            onClick={handleRequestEstimate}
            disabled={isEstimateRequested || isSubmitting}
          >
            {t("quest_estimate.button.request_estimate")}
          </ActionButton>
          <ActionButton
            onClick={handleSubmitOrder}
            disabled={!isEstimateRequested || isSubmitting}
          >
            {t("quest_estimate.button.submit_order")}
          </ActionButton>
        </Stack>
      </Box>
    </Stack>
  );
};

export default QuestEstimate;
